"use client";

import { PageHero } from "@widgets/page-hero";

import { LocationMapWithProvider } from "@features/map";
import { PointInnerContent } from "@features/point-inner-content";

import { usePointMap } from "@shared/hooks/use-point-map";
import cardsGrid from "@shared/ui/cards-grid/cards-grid.module.scss";
import { LocationMapCardLayout } from "@shared/ui/location-map-card-layout";
import { MapDetails } from "@shared/ui/map-details";
import { OtherPointsSection } from "@shared/ui/other-points-section";
import { PointCard } from "@shared/ui/point-card";

import type { MapPageType } from "@/_pages/map/model";

import type { AccommodationsPointType } from "../../model/types";

import s from "./single-accommodations-point-page.module.scss";

type Props = {
  point: AccommodationsPointType;
  mapData: Pick<
    MapPageType,
    "webcams" | "cafes-restaurants" | "accommodations" | "cultural-objects"
  >;
};

export const SingleAccommodationsPointPage = (props: Props) => {
  const { point, mapData } = props;

  const { mapPoints, focusLocation } = usePointMap({
    mapData,
    pointCoordinates: point?.coordinates,
    focusZoom: 16,
  });

  if (!point) return null;

  const otherAccommodations = point.other_accommodations;

  return (
    <main>
      {point.title && (
        <PageHero
          variant="secondary"
          textColor="brown"
          title={point.title}
          breadcrumbs={["accommodations"]}
        />
      )}
      <PointInnerContent data={point} />
      <section id="accommodations-map" className={s.container}>
        <LocationMapCardLayout
          title="Контактная информация"
          map={
            point.coordinates && (
              <LocationMapWithProvider
                mapPoints={mapPoints}
                primaryObjectId={point.documentId}
                focusLocation={focusLocation}
              />
            )
          }
        >
          <MapDetails
            location={point.location}
            workTimeList={point.workTimeList}
            phone={point.phone}
          />
        </LocationMapCardLayout>
      </section>
      {otherAccommodations && otherAccommodations.length > 0 && (
        <OtherPointsSection
          title="Другое жилье"
          buttonHref="/accommodations"
          buttonText="Смотреть все"
          buttonIconName="arrow"
          cardsClassName={cardsGrid.root}
        >
          {otherAccommodations?.map((card, cardIdx) =>
            card ? (
              <PointCard
                key={card?.uid ?? cardIdx}
                href={card?.uid ? `/accommodations/${card.uid}` : undefined}
                title={card?.title}
                description={card?.description}
                clampDescriptionOnMobile
                location={card?.location}
                phone={card?.phone}
                photos={card?.photos}
                filters={
                  card?.accommodations_type
                    ? [
                        {
                          uid: card.accommodations_type?.uid,
                          label: card.accommodations_type?.type,
                        },
                      ]
                    : []
                }
                badges={(card?.accommodations_badge_types ?? []).map((b) => ({
                  uid: b?.uid,
                  label: b?.type,
                }))}
                overlayIcon={{ name: "bed", fillColor: "violet" }}
              />
            ) : null,
          )}
        </OtherPointsSection>
      )}
    </main>
  );
};

SingleAccommodationsPointPage.displayName = "SingleAccommodationsPointPage";
